import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconCopy,
  IconLock,
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
} from '@tabler/icons-react';
import { ConfirmModal } from '../../../../shared/ui/ConfirmModal';
import type { Partner } from '../../../../entities/partner';
import { pb } from '../../../../shared/api/pocketbase';
import { Button } from '../../../../shared/ui/Button';
import { Input } from '../../../../shared/ui/Input';
import { PartnerModal } from '../../../../features/partner-form';

const PER_PAGE = 20;

const pluralPartners = (n: number) => {
  if (n === 1) return '1 партнёр';
  if (n < 5) return `${n} партнёра`;
  return `${n} партнёров`;
};

const buildPageNumbers = (current: number, total: number): (number | '...')[] => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '...')[] = [1];
  if (current > 3) pages.push('...');
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) {
    pages.push(p);
  }
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
};

const PartnersSection: React.FC = () => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState<{ mode: 'add' | 'edit'; partner?: Partner } | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState<Partner | null>(null);
  const [confirmBlock, setConfirmBlock] = useState<Partner | null>(null);
  const [blockingId, setBlockingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const partnerUrl = `${window.location.origin}/partner`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(partnerUrl).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 1500);
    });
  };

  const loadPartners = useCallback((p: number, q: string) => {
    setLoading(true);
    const safe = q.trim().replace(/"/g, '');
    const safeLower = safe.toLowerCase();
    let filter: string | undefined;
    if (safe) {
      if (safe.includes('@')) {
        filter = `email ~ "${safeLower}"`;
      } else {
        filter = `name ~ "${safe}" || name ~ "${safeLower}" || email ~ "${safeLower}"`;
      }
    }
    pb.collection('partners')
      .getList<Partner>(p, PER_PAGE, {
        sort: 'name',
        fields: 'id,name,email,sources,isBlocked,verified,created,updated',
        filter,
      })
      .then(result => {
        setPartners(result.items);
        setTotalPages(result.totalPages);
        setTotalItems(result.totalItems);
      })
      .catch(() => setPartners([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadPartners(1, ''); }, [loadPartners]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(1);
      loadPartners(1, value);
    }, 300);
  };

  const handlePageChange = (p: number) => {
    setPage(p);
    loadPartners(p, search);
  };

  const handleToggleBlock = useCallback((partner: Partner) => {
    const newIsBlocked = !partner.isBlocked;
    setBlockingId(partner.id);
    pb.collection('partners')
      .update(partner.id, { isBlocked: newIsBlocked })
      .then(() => setPartners(prev => prev.map(x => x.id === partner.id ? { ...x, isBlocked: newIsBlocked } : x)))
      .finally(() => setBlockingId(null));
  }, []);

  const handleDelete = useCallback((partner: Partner) => {
    setDeletingId(partner.id);
    pb.collection('partners')
      .delete(partner.id)
      .then(() => loadPartners(page, search))
      .finally(() => setDeletingId(null));
  }, [loadPartners, page, search]);

  const pageNumbers = buildPageNumbers(page, totalPages);

  return (
    <div className="adm-section">
      <div className="adm-section-header adm-section-header-row">
        <div>
          <h2 className="adm-section-title">Партнёры</h2>
          <p className="adm-section-sub">{loading ? 'Загрузка...' : pluralPartners(totalItems)}</p>
          <div className="adm-partner-link-row">
            <a href={partnerUrl} target="_blank" rel="noopener noreferrer" className="adm-partner-link">
              {partnerUrl}
            </a>
            <button type="button" className={`adm-partner-link-copy${copiedLink ? ' copied' : ''}`} onClick={handleCopyLink}>
              {copiedLink ? <IconCheck size={13} stroke={2.5} /> : <IconCopy size={13} stroke={2} />}
              {copiedLink ? 'Скопировано' : 'Копировать'}
            </button>
          </div>
        </div>
        <div className="adm-ut-toolbar">
          <div className="adm-input-wrap" style={{ width: 240 }}>
            <span className="adm-input-icon"><IconSearch size={15} stroke={2} /></span>
            <Input
              className="adm-input"
              placeholder="Поиск..."
              value={search}
              onChange={e => handleSearchChange(e.target.value)}
            />
          </div>
          <Button variant="adm-add" type="button" onClick={() => setModal({ mode: 'add' })}>
            <IconPlus size={15} stroke={2.5} />Добавить
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="adm-loading">Загрузка...</div>
      ) : (
        <>
          <div className="adm-ut-wrap">
            <table className="adm-ut">
              <thead>
                <tr>
                  <th>Партнёр</th>
                  <th>Источники</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {partners.map(partner => (
                  <tr key={partner.id} className="adm-ut-row">
                    <td>
                      <div className="adm-ut-info">
                        <span className="adm-ut-name">{partner.name || partner.email}</span>
                        {partner.name && <span className="adm-ut-email">{partner.email}</span>}
                        {partner.isBlocked && (
                          <span className="adm-role-badge blocked" style={{ marginTop: 2 }}>Заблокирован</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 300 }}>
                        {partner.sources && partner.sources.length > 0 ? (
                          <>
                            {partner.sources.slice(0, 3).map(s => (
                              <span key={s} className="adm-role-badge user">{s}</span>
                            ))}
                            {partner.sources.length > 3 && (
                              <span className="adm-role-badge" title={partner.sources.slice(3).join(', ')}>
                                +{partner.sources.length - 3}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="adm-ut-email">—</span>
                        )}
                      </div>
                    </td>
                    <td className="adm-ut-actions-cell">
                      <div className="adm-ut-actions">
                        <Button
                          variant="adm-edit"
                          type="button"
                          title="Редактировать"
                          onClick={() => setModal({ mode: 'edit', partner })}
                        >
                          <IconPencil size={13} stroke={2} />
                        </Button>
                        <Button
                          variant="adm-delete"
                          type="button"
                          title={partner.isBlocked ? 'Разблокировать' : 'Заблокировать'}
                          disabled={blockingId === partner.id}
                          onClick={() => setConfirmBlock(partner)}
                        >
                          <IconLock size={13} stroke={2} />
                        </Button>
                        <Button
                          variant="adm-delete"
                          type="button"
                          title="Удалить"
                          disabled={deletingId === partner.id}
                          onClick={() => setConfirmDelete(partner)}
                        >
                          <IconTrash size={13} stroke={2} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {partners.length === 0 && (
                  <tr><td colSpan={3} className="adm-empty">Партнёры не найдены</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="adm-pagination">
              <button
                className="adm-page-btn"
                disabled={page === 1}
                onClick={() => handlePageChange(page - 1)}
                type="button"
              >
                <IconChevronLeft size={15} stroke={2} />
              </button>
              {pageNumbers.map((p, i) =>
                p === '...'
                  ? <span key={`ellipsis-${i}`} className="adm-page-ellipsis">…</span>
                  : <button
                      key={p}
                      className={`adm-page-btn${p === page ? ' active' : ''}`}
                      onClick={() => handlePageChange(p)}
                      type="button"
                    >{p}</button>
              )}
              <button
                className="adm-page-btn"
                disabled={page === totalPages}
                onClick={() => handlePageChange(page + 1)}
                type="button"
              >
                <IconChevronRight size={15} stroke={2} />
              </button>
            </div>
          )}
        </>
      )}

      {modal && (
        <PartnerModal
          mode={modal.mode}
          initial={modal.partner ?? null}
          onClose={() => setModal(null)}
          onSaved={updated => {
            if (updated) {
              setPartners(prev => prev.map(p => p.id === updated.id ? updated : p));
            } else {
              loadPartners(page, search);
            }
          }}
        />
      )}

      {confirmBlock && (
        <ConfirmModal
          title={confirmBlock.isBlocked ? 'Разблокировать партнёра?' : 'Заблокировать партнёра?'}
          content={
            confirmBlock.isBlocked
              ? <>Партнёр <strong>{confirmBlock.name || confirmBlock.email}</strong> снова получит доступ.</>
              : <>Партнёр <strong>{confirmBlock.name || confirmBlock.email}</strong> потеряет доступ к системе.</>
          }
          onConfirm={() => { handleToggleBlock(confirmBlock); setConfirmBlock(null); }}
          onClose={() => setConfirmBlock(null)}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Удалить партнёра?"
          content={<>Вы уверены, что хотите удалить <strong>{confirmDelete.name || confirmDelete.email}</strong>? Это действие необратимо.</>}
          onConfirm={() => { handleDelete(confirmDelete); setConfirmDelete(null); }}
          onClose={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

export { PartnersSection };
